import React, { useState } from 'react';
import { Edit3, Save, Loader } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { Prospect } from './types';

interface NotesCardProps {
  prospect: Prospect;
  onProspectUpdate: (updatedProspect: Prospect) => void;
}

const NotesCard: React.FC<NotesCardProps> = ({ prospect, onProspectUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(prospect.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Vérifier si les notes ont changé
      if (notes === prospect.notes) {
        setIsEditing(false);
        return;
      }

      const { error, data } = await supabase
        .from('prospects')
        .update({ notes })
        .eq('id', prospect.id)
        .select();

      if (error) throw error;

      
      // Update the prospect with the new notes
      onProspectUpdate({ ...prospect, notes });
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des notes:', error);
      alert(`Erreur lors de la sauvegarde des notes: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(prospect.notes || '');
    setIsEditing(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Notes</span>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800"
          >
            <Edit3 className="w-3 h-3 mr-1" />
            Modifier
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ajouter des notes sur ce prospect..."
            className="w-full p-3 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            rows={4}
          />
          <div className="flex justify-end space-x-2">
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader className="w-3 h-3 mr-1 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-3 h-3 mr-1" />
                  Sauvegarder
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div>
          {notes ? (
            <div className="bg-gray-50 p-3 rounded-md border">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{notes}</p>
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic">Aucune note ajoutée</div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotesCard;
